import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import ScoutMindLogo from '../../components/Logo/ScoutMindLogo';
import { publicApi } from '../../services/apiClient';
import toast from 'react-hot-toast';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error(t('auth.forgotPasswordPage.errors.emailRequired'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error(t('auth.forgotPasswordPage.errors.emailInvalid'));
      return;
    }

    setIsLoading(true);

    try {
      await publicApi.post('/auth/forgot-password', {
        email: email.trim().toLowerCase()
      });

      setIsSuccess(true);
      toast.success(t('auth.forgotPasswordPage.errors.genericError'));
      
    } catch (error: any) {
      toast.error(
        error.response?.data?.mensaje || 
        t('auth.forgotPasswordPage.errors.requestError')
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 p-8 text-center">
            <div className="flex justify-center mb-8">
              <ScoutMindLogo className="h-12 w-auto" />
            </div>

            <div className="mb-6">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">
                {t('auth.forgotPasswordPage.success.title')}
              </h1>
              <p className="text-blue-100 text-sm leading-relaxed">
                {t('auth.forgotPasswordPage.success.message', { email: email })}
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-500/20 border border-blue-400/30 rounded-lg p-4 text-left">
                <h3 className="text-sm font-semibold text-blue-200 mb-2">
                  {t('auth.forgotPasswordPage.success.noEmailReceived')}
                </h3>
                <ul className="text-xs text-blue-100 space-y-1">
                  <li>• {t('auth.forgotPasswordPage.success.tip1')}</li>
                  <li>• {t('auth.forgotPasswordPage.success.tip2')}</li>
                  <li>• {t('auth.forgotPasswordPage.success.tip3')}</li>
                </ul>
              </div>

              <div className="flex flex-col space-y-3">
                <button
                  onClick={() => {
                    setIsSuccess(false);
                    setEmail('');
                  }}
                  className="w-full py-3 px-4 text-white font-medium rounded-lg transition-colors"
                  style={{ background: '#0F2A7D' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#0A1F5C'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#0F2A7D'}
                >
                  {t('auth.forgotPasswordPage.success.sendNewLink')}
                </button>
                
                <Link
                  to="/login"
                  className="w-full py-3 px-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors text-center border border-white/20"
                >
                  {t('auth.forgotPasswordPage.success.backToLoginSuccess')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <ScoutMindLogo className="h-12 w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {t('auth.forgotPasswordPage.title')}
            </h1>
            <p className="text-blue-100 text-sm">
              {t('auth.forgotPasswordPage.subtitle')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {t('auth.forgotPasswordPage.email')} *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                  placeholder={t('auth.forgotPasswordPage.emailPlaceholder')}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{ background: '#0F2A7D' }}
              onMouseEnter={(e) => !isLoading && (e.currentTarget.style.background = '#0A1F5C')}
              onMouseLeave={(e) => e.currentTarget.style.background = '#0F2A7D'}
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <LoadingSpinner size="sm" />
                  <span>{t('auth.forgotPasswordPage.sending')}</span>
                </div>
              ) : (
                t('auth.forgotPasswordPage.sendResetLink')
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <Link
              to="/login"
              className="inline-flex items-center space-x-2 text-blue-300 hover:text-blue-200 text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('auth.forgotPasswordPage.backToLogin')}</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;